import CircleIcon from "@mui/icons-material/Circle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Rating,
  Skeleton
} from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { JobPricingReportConstants } from "../../../constants/JobPricingReportConstants";
import { getLanguageCode } from "../../../utilities/helper";
import {
  BehavioralCompetenciesData,
  SkillsData,
  SuccessProfileDetailsQueryParams
} from "../../../helpers/models/JobPricingDetailModels";
import { RootState } from "../../../store/reducers";
import {
  getSuccessProfileDataThunk,
  setViewSuccessProfile
} from "../../../store/slices/jobPricingDetailsSlice";
import "./ReportSuccessProfile.scss";

export const ReportSuccessProfile = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    isViewSuccessProfile,
    headerDetails,
    successProfileDetails,
    isSuccessProfileDetailsLoading
  } = useSelector((state: RootState) => state.jobPricingDetails);
  const { selectedCountry } = useSelector((state: RootState) => state.country);

  const handleClose = () => {
    dispatch(setViewSuccessProfile(false));
  };

  const getSuccessProfileDetails = () => {
    if (!isSuccessProfileDetailsLoading && !successProfileDetails) {
      const queryParams: SuccessProfileDetailsQueryParams = {
        countrycode: selectedCountry?.key || "",
        language: getLanguageCode(),
        jobcode: headerDetails?.jobCode || "",
        grade: headerDetails?.grade || ""
      };
      dispatch(getSuccessProfileDataThunk(queryParams));
    }
  };

  useEffect(() => {
    if (isViewSuccessProfile) {
      getSuccessProfileDetails();
    }
  }, [isViewSuccessProfile]);

  useEffect(() => {
    if (headerDetails && Object.keys(headerDetails).length > 0) {
      getSuccessProfileDetails();
    }
  }, [headerDetails]);
  return (
    <div className="sjpr-success-profile-container">
      <Dialog
        open={isViewSuccessProfile}
        onClose={handleClose}
        scroll="paper"
        fullWidth={true}
        maxWidth={"md"}
        className="sjpr-success-profile-dialog"
        aria-labelledby="success-profile-dialog-label"
        aria-describedby="success-profile-dialog-description"
      >
        <div className="success-profile-dialog-title">
          <DialogTitle
            id="success-profile-dialog-title"
            sx={{ fontWeight: "700", paddingX: "16px" }}
          >
            {headerDetails.title}
          </DialogTitle>
          <IconButton
            aria-label="close"
            title="Close"
            disableRipple
            onClick={handleClose}
            sx={{ padding: "16px 24px" }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent dividers={true}>
          <DialogContentText
            id="success-profile-dialog-content-text"
            tabIndex={-1}
          >
            <span className="dialog-content-text">
              <span className="content-text-title mb-5">
                {t("pa.jobSummary")}
              </span>
              <span className="content-text-description">
                {isSuccessProfileDetailsLoading ? (
                  <>
                    {[1, 2, 3].map((value: number, index: number) => (
                      <Skeleton key={index} />
                    ))}
                  </>
                ) : (
                  (successProfileDetails || {})?.jobSummary ||
                  JobPricingReportConstants.NO_DATA_AVAILABLE_MSG
                )}
              </span>
            </span>
            <span className="dialog-content-text">
              <span className="content-text-title">{t("pa.skills")}</span>
              <span className="content-text-description">
                <span className="content-skills-container">
                  {isSuccessProfileDetailsLoading ? (
                    <>
                      {[1, 2, 3].map((value: number, index: number) => (
                        <span className="content-skill" key={index}>
                          <Skeleton width={"100%"} />
                        </span>
                      ))}
                    </>
                  ) : (
                    <>
                      {successProfileDetails &&
                        successProfileDetails?.skills &&
                        successProfileDetails?.skills.length > 0 && (
                          <>
                            {successProfileDetails.skills.map(
                              (data: SkillsData, index: number) => (
                                <span className="content-skill" key={index}>
                                  <span
                                    className="content-text-description"
                                    style={{ marginBottom: 0 }}
                                  >
                                    {data.name}
                                  </span>
                                  <span className="content-skill-rate">
                                    <Rating
                                      name="rate-circle"
                                      readOnly
                                      size="small"
                                      defaultValue={data.level || 0}
                                      max={5}
                                      icon={<CircleIcon fontSize="inherit" />}
                                      emptyIcon={
                                        <CircleOutlinedIcon fontSize="inherit" />
                                      }
                                    />
                                  </span>
                                </span>
                              )
                            )}
                          </>
                        )}
                      {(!successProfileDetails ||
                        !successProfileDetails?.skills ||
                        successProfileDetails?.skills.length === 0) && (
                        <span>
                          {JobPricingReportConstants.NO_DATA_AVAILABLE_MSG}
                        </span>
                      )}
                    </>
                  )}
                </span>
              </span>
            </span>
            <span className="dialog-content-text">
              <span className="content-text-title">
                {t("pa.behavioralCompetencies")}
              </span>
              <span className="content-text-description">
                <span className="content-behavioral-competencies-container">
                  {isSuccessProfileDetailsLoading ? (
                    <>
                      {[1, 2, 3].map((value: number, index: number) => (
                        <span className="content-skill" key={index}>
                          <Skeleton width={"100%"} />
                        </span>
                      ))}
                    </>
                  ) : (
                    <>
                      {successProfileDetails &&
                        successProfileDetails?.behavioralCompetencies &&
                        successProfileDetails?.behavioralCompetencies.length >
                          0 && (
                          <>
                            {successProfileDetails.behavioralCompetencies.map(
                              (
                                data: BehavioralCompetenciesData,
                                index: number
                              ) => (
                                <span className="content-skill" key={index}>
                                  <span
                                    className="content-text-description"
                                    style={{ marginBottom: 0 }}
                                  >
                                    {data.name}
                                  </span>
                                  <span className="content-skill-rate">
                                    <Rating
                                      name="rate-circle"
                                      readOnly
                                      size="small"
                                      defaultValue={data.level || 0}
                                      max={5}
                                      icon={<CircleIcon fontSize="inherit" />}
                                      emptyIcon={
                                        <CircleOutlinedIcon fontSize="inherit" />
                                      }
                                    />
                                  </span>
                                </span>
                              )
                            )}
                          </>
                        )}
                      {(!successProfileDetails ||
                        !successProfileDetails?.behavioralCompetencies ||
                        successProfileDetails?.behavioralCompetencies.length ===
                          0) && (
                        <span>
                          {JobPricingReportConstants.NO_DATA_AVAILABLE_MSG}
                        </span>
                      )}
                    </>
                  )}
                </span>
              </span>
            </span>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              backgroundColor: "var( --primary-color)",
              color: "var(--contained-button-text)",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "var(--primary-color)"
              }
            }}
          >
            {t("pa.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
